import { useState, useRef, useEffect } from "react";
import { C, API_BASE, getFontFamily } from "../constants/colors";
import { LANG } from "../constants/lang";
import { askClaude } from "../helpers";
import { RevisionText, Dots } from "./ui";

export function RevisionPanel({ problem, lastTutorMsg, onClose, lang, studentClass, board, autoConceptName }) {
  const t = LANG[lang];
  const ff = lang==="hi"?"'Noto Sans Devanagari','Outfit',sans-serif":"'Outfit',sans-serif";
  const [concept, setConcept] = useState("");
  const [explanation, setExplanation] = useState("");
  const [loading, setLoading] = useState(true);
  const [typed, setTyped] = useState(false);
  const [custom, setCustom] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => { return () => { mountedRef.current = false; }; }, []);

  const detectAndFetch = async (forcedConcept) => {
    setLoading(true); setTyped(false); setExplanation(""); setShowSearch(false);
    let target = forcedConcept;
    if (!target) {
      const detectPrompt = lang==="hi"
        ? `यह बातचीत देखें और बताएं कि छात्र को कौन सी एक अवधारणा समझने की जरूरत है। सिर्फ अवधारणा का नाम दें — कोई व्याख्या नहीं।`
        : `Look at this conversation and identify the single concept the student needs to revise. Reply with ONLY the concept name — nothing else.`;
      const detected = await askClaude([{
        role:"user",
        content:`Problem: "${problem}"\nLast tutor message: "${lastTutorMsg}"\n\n${detectPrompt}`
      }], {revision:true, studentClass, board, lang});
      if(!mountedRef.current) return;
      target = detected.trim().replace(/["""'']/g,"").replace(/\.$/,"");
    }
    if(!mountedRef.current) return;
    setConcept(target);
    const text = await askClaude(
      [{role:"user",content:`Problem: "${problem}"\nStudent: Class ${studentClass} ${board||"CBSE"}\nExplain concept: "${target}"`}],
      {revision:true, studentClass, board, lang}
    );
    if(!mountedRef.current) return;
    setExplanation(text);
    setLoading(false);
  };

  useEffect(() => { detectAndFetch(autoConceptName||null); }, []);

  return (
    <div onClick={(e)=>e.target===e.currentTarget&&onClose()} style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(15,23,42,0.55)",zIndex:200,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
      <div style={{background:C.surface,borderRadius:"24px 24px 0 0",width:"100%",maxWidth:640,maxHeight:"88vh",overflowY:"auto",animation:"su .3s cubic-bezier(.16,1,.3,1)"}}>
        <div style={{display:"flex",justifyContent:"center",padding:"12px 0 0"}}><div style={{width:36,height:4,background:C.border,borderRadius:2}}/></div>
        <div className="revision-panel" style={{padding:"20px 24px 44px",fontFamily:ff}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div>
              <div style={{fontSize:20,fontWeight:700,color:C.text}}>{t.reviseTitle}</div>
              <div style={{fontSize:13,color:C.textMute,marginTop:3}}>
                {loading ? (lang==="hi"?"अवधारणा पहचानी जा रही है...":"Detecting concept...") : concept}
              </div>
            </div>
            <button onClick={()=>onClose()} style={{background:C.borderLight,border:"none",borderRadius:12,width:36,height:36,cursor:"pointer",fontSize:18,color:C.textMute}}>✕</button>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:20,flexWrap:"wrap"}}>
            <div style={{display:"inline-flex",alignItems:"center",gap:6,background:C.primarySoft,border:`1px solid ${C.primaryLight}`,borderRadius:20,padding:"4px 12px",fontSize:12,color:C.primaryDark,fontWeight:600}}>
              🏫 Class {studentClass} · {board||"CBSE"}
            </div>
            {!loading&&concept&&(
              <button onClick={()=>setShowSearch(s=>!s)} style={{background:"none",border:"none",fontSize:12,color:C.textMute,cursor:"pointer",textDecoration:"underline",fontFamily:ff}}>
                {lang==="hi"?"अलग अवधारणा खोजें":"Search different concept"}
              </button>
            )}
          </div>
          {showSearch&&(
            <div style={{display:"flex",gap:8,marginBottom:16}}>
              <input value={custom} onChange={(e)=>setCustom(e.target.value)}
                onKeyDown={(e)=>e.key==="Enter"&&custom.trim()&&detectAndFetch(custom.trim())}
                placeholder={t.reviseInputPlaceholder}
                style={{flex:1,background:"#f8fafc",border:`1.5px solid ${C.border}`,borderRadius:12,padding:"10px 14px",fontSize:14,color:C.text,outline:"none",fontFamily:ff}}
                onFocus={(e)=>e.target.style.borderColor=C.primary} onBlur={(e)=>e.target.style.borderColor=C.border}
                autoFocus
              />
              <button onClick={()=>custom.trim()&&detectAndFetch(custom.trim())} disabled={!custom.trim()} style={{background:custom.trim()?C.primary:C.border,color:custom.trim()?"#fff":C.textMute,border:"none",borderRadius:12,padding:"10px 18px",cursor:custom.trim()?"pointer":"not-allowed",fontWeight:600,fontSize:14,fontFamily:ff}}>{t.goBtn}</button>
            </div>
          )}

          {/* Explanation */}
          <div style={{background:"#f8fafc",border:`1px solid ${C.border}`,borderRadius:16,padding:"20px 22px",minHeight:80}}>
            {loading
              ? <Dots label={lang==="hi"?"समझ रहा है...":concept?`Loading ${concept}...`:"Detecting concept..."}/>
              : <RevisionText text={explanation} onDone={()=>setTyped(true)}/>
            }
          </div>

          {typed&&(
            <button onClick={()=>onClose(explanation)} style={{marginTop:16,width:"100%",background:C.primary,color:"#fff",border:"none",borderRadius:14,padding:"14px",fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:ff}}>
              {t.gotItBtn} →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Photo Upload ──────────────────────────────────────────────────────────────
